import { useNavigate } from "react-router-dom";

const UPDATED = "August 2026";

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="text-2xl text-white leading-tight">{title}</h2>
      <div className="space-y-3 text-slate-400 leading-relaxed">{children}</div>
    </section>
  );
}

export default function Terms() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#07090E] text-slate-200">
      {/* NAV */}
      <nav className="sticky top-0 z-40 bg-[#07090E]/85 backdrop-blur-xl border-b border-[#1E273D]">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="terms-home">
            <img src="/openword-logo.png" alt="OpenWord AI" className="w-8 h-8" />
            <span className="text-xl text-white">OpenWord AI</span>
          </button>
          <button onClick={() => navigate("/")} className="text-sm text-slate-400 hover:text-[#00E5FF] transition-colors">
            Back to home
          </button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-16 space-y-12">
        <header className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF]">Terms of Service</div>
          <h1 className="text-4xl sm:text-5xl text-white leading-tight">A few honest terms.</h1>
          <p className="text-lg text-slate-400 leading-relaxed">
            These terms explain what OpenWord AI is, what you can expect from it, and what we ask of you. By using
            OpenWord AI, you agree to them. We've tried to keep them plain and fair.
          </p>
          <p className="text-sm text-slate-500">Last updated: {UPDATED}</p>
        </header>

        <Section title="1. About OpenWord AI">
          <p>
            OpenWord AI is a Bible‑study tool available at openwordai.com, Powered by OpenWord AI
            (“OpenWord AI,” “we,” “us”). It provides Scripture, original‑language references, notes, and an
            virtual study companion to help you engage the biblical text. OpenWord AI is a study tool — it is{" "}
            <strong className="text-slate-200">not</strong> a church, a pastor, a counselor, spiritual direction, or
            a replacement for the Bible itself.
          </p>
        </Section>

        <Section title="2. Accepting these terms">
          <p>
            By creating an account or using OpenWord AI, you agree to these Terms of Service and to our Privacy
            Policy. If you do not agree, please do not use OpenWord AI.
          </p>
        </Section>

        <Section title="3. Who may use OpenWord AI">
          <p>
            You must be at least 13 years old to use OpenWord AI. If you are under the age of majority where you live,
            you should use OpenWord AI with the involvement of a parent or guardian. By using OpenWord AI, you confirm
            you meet these requirements.
          </p>
        </Section>

        <Section title="4. Your account">
          <ul className="space-y-3">
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>You are responsible for keeping your login credentials secure and for activity under your account.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>Provide accurate information when you sign up, and let us know if you believe your account has been compromised.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>You may close your account at any time by contacting us.</span></li>
          </ul>
        </Section>

        <Section title="5. Acceptable use">
          <p>When using OpenWord AI, please do not:</p>
          <ul className="space-y-3">
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>Break the law, infringe others' rights, or use OpenWord AI to harm, harass, or deceive others.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>Attempt to disrupt, overload, reverse‑engineer, or gain unauthorized access to the service or its systems.</span></li>
            <li className="flex gap-3"><span className="text-[#00E5FF]">—</span><span>Misuse the virtual study companion to generate unlawful, harmful, or abusive content, or to scrape or resell the service.</span></li>
          </ul>
          <p>We may suspend or close accounts that violate these terms or put the service or others at risk.</p>
        </Section>

        <Section title="6. The virtual study companion — important disclaimer">
          <p>
            The virtual study companion is powered by third‑party artificial intelligence. It is a tool to help you engage
            Scripture; it is not infallible and can make mistakes, including in its explanations, historical
            details, or original‑language notes. It does not speak for God, and it is not a substitute for the
            Bible, for your own study and prayer, for a pastor, or for your church community.
          </p>
          <p>
            Always weigh what the virtual study companion says against Scripture itself. OpenWord AI's guiding conviction is that
            Scripture is the authority — the virtual study companion points to the text, but the text is the standard. Do not
            rely on the virtual study companion for medical, legal, financial, mental‑health, or crisis needs; seek a qualified
            professional or, in an emergency, your local emergency services.
          </p>
        </Section>

        <Section title="7. Your content">
          <p>
            Your notes, reflections, questions, and other content you create in OpenWord AI remain yours. You grant us
            the limited permission needed to store, display, and process that content to operate the service for
            you — including sending relevant text to our AI provider so the virtual study companion can respond. We do not claim
            ownership of your writing and we do not sell it.
          </p>
        </Section>

        <Section title="8. Scripture and intellectual property">
          <p>
            Bible translations available in OpenWord AI are provided through third parties. Some translations are in
            the public domain; others (such as the NIV, NKJV, and AMP) are copyrighted by their respective
            publishers and are used under license, subject to those publishers' terms and notices, which appear
            with the text. The OpenWord AI name, logo, design, and software are the property of OpenWord AI
             and may not be copied or used without permission.
          </p>
        </Section>

        <Section title="9. Third‑party services">
          <p>
            OpenWord AI relies on third‑party providers (for AI generation, Scripture text, email, sign‑in,
            payments, analytics, and hosting). Your use of features that depend on them is also subject to those providers' terms, and we
            are not responsible for their services or availability.
          </p>
        </Section>

        <Section title="10. Service availability and changes">
          <p>
            We work to keep OpenWord AI running, but we provide it “as is” and may change, suspend, or discontinue
            features at any time. Some capabilities depend on external services and quotas and may occasionally be
            unavailable.
          </p>
        </Section>

        <Section title="11. Disclaimers and limitation of liability">
          <p>
            To the fullest extent permitted by law, OpenWord AI is provided “as is” and “as available,” without
            warranties of any kind, express or implied. We do not warrant that the service or the virtual study companion's
            output will be accurate, uninterrupted, or error‑free.
          </p>
          <p>
            To the fullest extent permitted by law, OpenWord AI will not be liable for
            any indirect, incidental, or consequential damages, or for any decisions you make in reliance on the
            service or the virtual study companion's output.
          </p>
        </Section>

        <Section title="12. Billing, cancellation, and refunds">
          <p>
            Paid plans renew automatically each month until you cancel. You can cancel anytime from your account
            settings — cancellation takes effect at the end of your current billing month, and you won&apos;t be
            charged again after that. We don&apos;t refund the unused portion of a month you&apos;ve already paid for.
          </p>
          <p>
            One‑time credit top‑ups are non‑refundable once any part of the balance has been used.
          </p>
        </Section>

        <Section title="13. Termination">
          <p>
            You may stop using OpenWord AI at any time. We may suspend or terminate access if you violate these terms
            or to protect the service or its users. On termination, the provisions that by their nature should
            survive (such as intellectual property, disclaimers, and limitation of liability) will continue to
            apply.
          </p>
        </Section>

        <Section title="14. Changes to these terms">
          <p>
            We may update these terms as OpenWord AI develops. When we make material changes, we will update the
            “Last updated” date above. Your continued use of OpenWord AI after changes take effect means you accept
            the updated terms.
          </p>
        </Section>

        <Section title="15. Connecting a Facebook Page (Page administrators)">
          <p>
            If you connect a Facebook Page to OpenWord AI, you confirm that you are an authorized administrator of
            that Page and that you have the right to grant OpenWord AI access to read and reply to comments on it.
            You are responsible for the replies published to your Page, including any published automatically when
            you enable auto‑reply. Your use of this feature must also comply with Meta's Platform Terms and the
            Facebook Community Standards. We access only the Page(s) you connect, and you may disconnect at any time
            from the admin dashboard or via Facebook → Settings → Business Integrations, which revokes our access.
          </p>
        </Section>

        <Section title="16. Contact us">
          <p>
            Questions about these terms? Reach us through our{" "}
            <a href="/support" className="text-[#00E5FF] hover:underline">support form</a>.
          </p>
          <p className="text-sm text-slate-500">Powered by OpenWord AI.</p>
        </Section>
      </main>

      <footer className="border-t border-[#1E273D] bg-[#0E131F]">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between text-sm text-slate-500">
          <button onClick={() => navigate("/")} className="text-lg text-slate-200 hover:text-[#00E5FF]">OpenWord AI</button>
          <span className="text-xs text-slate-500">© 2026 Powered by OpenWord AI</span>
        </div>
      </footer>
    </div>
  );
}
